/* Tax Center admin panel — business tax profile + per-author TINs +
 * regional sales ledger + EBITDA snapshot + min-price guard.
 * All data comes from `/api/admin/tax-center/*` endpoints.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Landmark, RefreshCw, Save, Globe, AlertTriangle, Loader2, TrendingUp, Calendar, Upload, Download, Search, X } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

import { openConfirm, openPrompt } from '@/components/admin/ConfirmDialog';

const TAX_FORMS = ['W-9', 'W-8BEN', 'W-8BEN-E'];
const ENTITY_TYPES = ['LLC', 'Corporation', 'Partnership', 'Sole Proprietor', 'Foreign'];

function Section({ title, icon: Icon, children }) {
  return (
    <section className="rounded-lg border border-border bg-card p-4">
      <div className="mb-3 flex items-center gap-2 text-[13px] font-semibold tracking-tight">
        {Icon && <Icon className="h-3.5 w-3.5 text-primary" />}
        {title}
      </div>
      {children}
    </section>
  );
}

function Field({ label, hint, children, testid }) {
  return (
    <label className="block" data-testid={testid ? `field-${testid}` : undefined}>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      {children}
      {hint && <div className="mt-0.5 text-[10.5px] text-muted-foreground">{hint}</div>}
    </label>
  );
}

const inputCls = 'mt-1 h-8 w-full rounded-sm border border-border bg-background px-2 text-[12.5px]';

export default function TaxCenterPanel() {
  const [profile, setProfile] = useState(null);
  const [sales, setSales] = useState(null);
  const [ebitda, setEbitda] = useState(null);
  const [ebitdaBook, setEbitdaBook] = useState(null);
  const [guard, setGuard] = useState(null);
  const [presets, setPresets] = useState([]);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState('profile');

  const load = async () => {
    setBusy(true);
    try {
      const [p, s, e, eb, g, pr] = await Promise.all([
        http.get('/admin/tax-center/profile'),
        http.get('/admin/tax-center/regional-sales'),
        http.get('/admin/tax-center/ebitda'),
        http.get('/admin/tax-center/ebitda-per-book'),
        http.get('/admin/tax-center/min-price-guard'),
        http.get('/admin/compliance-calendar/portal-presets'),
      ]);
      setProfile(p.data.profile);
      setSales(s.data);
      setEbitda(e.data);
      setEbitdaBook(eb.data);
      setGuard(g.data);
      setPresets(pr.data.presets || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load Tax Center.');
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...profile };
      // Convert masked back to blank so we don't overwrite with mask.
      ['ein_masked', 'ssn_or_itin_masked', 'foreign_tin_masked'].forEach((k) => delete payload[k]);
      const r = await http.put('/admin/tax-center/profile', payload);
      setProfile(r.data.profile);
      toast.success('Tax profile saved.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed.');
    } finally {
      setBusy(false);
    }
  };

  const enforceMinPrice = async (dry = true) => {
    try {
      const r = await http.post('/admin/tax-center/min-price-guard/enforce', { dry_run: dry });
      toast.success(dry
        ? `Would snap ${r.data.changed} book(s) up to floor.`
        : `Snapped ${r.data.changed} book(s) up to viability floor.`);
      if (!dry) await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Enforce failed.');
    }
  };

  if (!profile) {
    return (
      <div className="flex h-40 items-center justify-center text-[12px] text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading Tax Center…
      </div>
    );
  }

  const TABS = [
    { v: 'profile',  label: 'Business Profile' },
    { v: 'entity',   label: 'Business Entity' },
    { v: 'calendar', label: 'Compliance Calendar' },
    { v: 'sales',    label: 'Regional Sales' },
    { v: 'ebitda',   label: 'EBITDA / EBITDAT' },
    { v: 'guard',    label: 'Min-Price Guard' },
  ];

  return (
    <div className="space-y-4" data-testid="admin-tax-panel">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-[16px] font-semibold tracking-tight">
            <Landmark className="h-4 w-4 text-primary" /> Tax Center
          </h2>
          <p className="mt-0.5 text-[11.5px] text-muted-foreground">
            Business tax profile, regional sales, live EBITDA, and store-price
            viability floor. Data feeds Financial Statements and the min-price guard.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={busy}
          data-testid="admin-tax-refresh"
          className="inline-flex items-center gap-1.5 rounded-sm border border-border bg-background px-2.5 py-1.5 text-[11.5px] hover:bg-secondary disabled:opacity-60"
        >
          <RefreshCw className={`h-3 w-3 ${busy ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {TABS.map((t) => (
          <button
            key={t.v}
            type="button"
            onClick={() => setTab(t.v)}
            data-testid={`admin-tax-tab-${t.v}`}
            className={`rounded-sm border px-3 py-1 text-[11.5px] transition ${tab === t.v
              ? 'border-primary bg-primary/10 text-primary'
              : 'border-border bg-card text-muted-foreground hover:bg-secondary'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'profile' && (
        <Section title="Business Tax Profile" icon={Landmark}>
          <div className="grid gap-3 md:grid-cols-2">
            <Field label="Legal entity name" testid="legal-name">
              <input className={inputCls} value={profile.legal_entity_name || ''}
                     data-testid="tax-legal-name"
                     onChange={(e) => setProfile({ ...profile, legal_entity_name: e.target.value })} />
            </Field>
            <Field label="Entity type">
              <select className={inputCls} value={profile.entity_type || 'LLC'}
                      data-testid="tax-entity-type"
                      onChange={(e) => setProfile({ ...profile, entity_type: e.target.value })}>
                {ENTITY_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="State of registration">
              <input className={inputCls} value={profile.registration_state || ''}
                     placeholder="e.g. FL"
                     data-testid="tax-state"
                     onChange={(e) => setProfile({ ...profile, registration_state: e.target.value })} />
            </Field>
            <Field label="Country">
              <input className={inputCls} value={profile.registration_country || 'US'}
                     data-testid="tax-country"
                     onChange={(e) => setProfile({ ...profile, registration_country: e.target.value })} />
            </Field>
            <Field label="EIN" hint="Employer Identification Number">
              <input className={inputCls}
                     placeholder={profile.ein_masked || '—'}
                     data-testid="tax-ein"
                     onChange={(e) => setProfile({ ...profile, ein: e.target.value })} />
            </Field>
            <Field label="SSN / ITIN" hint="Sole proprietors only">
              <input className={inputCls} type="password"
                     placeholder={profile.ssn_or_itin_masked || '—'}
                     data-testid="tax-ssn"
                     onChange={(e) => setProfile({ ...profile, ssn_or_itin: e.target.value })} />
            </Field>
            <Field label="Primary tax form">
              <select className={inputCls} value={profile.tax_form_us || 'W-9'}
                      data-testid="tax-form"
                      onChange={(e) => setProfile({ ...profile, tax_form_us: e.target.value })}>
                {TAX_FORMS.map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Foreign TIN" hint="If non-US">
              <input className={inputCls} placeholder={profile.foreign_tin_masked || '—'}
                     data-testid="tax-foreign-tin"
                     onChange={(e) => setProfile({ ...profile, foreign_tin: e.target.value })} />
            </Field>
            <Field label="DUNS number" hint="9-digit D&B ID">
              <input className={inputCls} value={profile.duns_number || ''}
                     data-testid="tax-duns"
                     onChange={(e) => setProfile({ ...profile, duns_number: e.target.value })} />
            </Field>
            <Field label="Minimum store price (¢)" hint="Floor across the storefront">
              <input className={inputCls} type="number" min="99" max="99999"
                     value={profile.min_store_price_cents || 299}
                     data-testid="tax-min-price"
                     onChange={(e) => setProfile({ ...profile, min_store_price_cents: parseInt(e.target.value || '0', 10) })} />
            </Field>
            <Field label="Target margin %" hint="Used by the min-price guard">
              <input className={inputCls} type="number" min="0" max="95"
                     value={profile.target_margin_pct || 40}
                     data-testid="tax-target-margin"
                     onChange={(e) => setProfile({ ...profile, target_margin_pct: parseInt(e.target.value || '0', 10) })} />
            </Field>
          </div>
          <div className="mt-3 rounded-md border border-dashed border-border bg-panel-header/40 p-3">
            <div className="mb-1 flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground">
              <Globe className="h-3 w-3" /> Per-country TINs (India PAN, Taiwan VAT, Brazil CPF…)
            </div>
            <textarea
              rows={3}
              className="w-full rounded-sm border border-border bg-background p-2 text-[12px] font-mono"
              placeholder='IN=PAN12345K, TW=VAT456, BR=CPF...'
              data-testid="tax-per-country-tins"
              value={Object.entries(profile.per_country_tins || {}).map(([k, v]) => `${k}=${v}`).join(', ')}
              onChange={(e) => {
                const map = {};
                (e.target.value || '').split(/[,\n]/).forEach((row) => {
                  const [k, ...rest] = row.trim().split('=');
                  if (k && rest.length) map[k.toUpperCase().slice(0, 3)] = rest.join('=').trim();
                });
                setProfile({ ...profile, per_country_tins: map });
              }}
            />
          </div>
          <div className="mt-3 flex gap-2">
            <button
              type="button" onClick={save} disabled={busy}
              data-testid="admin-tax-save"
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-[12.5px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
            >
              <Save className="h-4 w-4" /> Save profile
            </button>
          </div>
        </Section>
      )}

      {tab === 'entity' && (
        <Section title="Business Entity · Licenses & Annual Fees" icon={Landmark}>
          <div className="grid gap-3 md:grid-cols-3">
            <Metric label="Legal entity" value={profile.legal_entity_name || '—'} />
            <Metric label="EIN" value={profile.ein_masked || (profile.ein ? '(set)' : '—')} />
            <Metric label="DUNS" value={profile.duns_number || '—'} />
          </div>

          <div className="mt-4">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-[12px] font-semibold">Business licenses</div>
              <button
                type="button"
                data-testid="tax-license-add"
                onClick={() => setProfile({ ...profile,
                  business_licenses: [...(profile.business_licenses || []),
                    { label: '', number: '', jurisdiction: '', issued_on: '', expires_on: '', document_url: '' }] })}
                className="rounded-sm border border-primary/40 bg-primary/10 px-2 py-1 text-[10.5px] text-primary hover:bg-primary/20"
              >+ Add license</button>
            </div>
            {(profile.business_licenses || []).length === 0 && (
              <div className="rounded-md border border-dashed border-border p-3 text-[11.5px] text-muted-foreground">
                No business licenses on file yet.
              </div>
            )}
            {(profile.business_licenses || []).map((lic, idx) => (
              <div key={idx} className="mb-2 grid gap-2 rounded-md border border-border p-2 md:grid-cols-3" data-testid={`tax-license-row-${idx}`}>
                <input className={inputCls} placeholder="Label (e.g. Local business license)"
                       value={lic.label || ''} data-testid={`tax-license-label-${idx}`}
                       onChange={(e) => {
                         const rows = [...profile.business_licenses];
                         rows[idx] = { ...rows[idx], label: e.target.value };
                         setProfile({ ...profile, business_licenses: rows });
                       }} />
                <input className={inputCls} placeholder="License number"
                       value={lic.number || ''} data-testid={`tax-license-number-${idx}`}
                       onChange={(e) => {
                         const rows = [...profile.business_licenses];
                         rows[idx] = { ...rows[idx], number: e.target.value };
                         setProfile({ ...profile, business_licenses: rows });
                       }} />
                <input className={inputCls} placeholder="Jurisdiction (state / city)"
                       value={lic.jurisdiction || ''}
                       onChange={(e) => {
                         const rows = [...profile.business_licenses];
                         rows[idx] = { ...rows[idx], jurisdiction: e.target.value };
                         setProfile({ ...profile, business_licenses: rows });
                       }} />
                <input className={inputCls} placeholder="Issued on YYYY-MM-DD"
                       value={lic.issued_on || ''}
                       onChange={(e) => {
                         const rows = [...profile.business_licenses];
                         rows[idx] = { ...rows[idx], issued_on: e.target.value };
                         setProfile({ ...profile, business_licenses: rows });
                       }} />
                <input className={inputCls} placeholder="Expires on YYYY-MM-DD"
                       value={lic.expires_on || ''} data-testid={`tax-license-expires-${idx}`}
                       onChange={(e) => {
                         const rows = [...profile.business_licenses];
                         rows[idx] = { ...rows[idx], expires_on: e.target.value };
                         setProfile({ ...profile, business_licenses: rows });
                       }} />
                <input className={inputCls} placeholder="Document URL"
                       value={lic.document_url || ''}
                       onChange={(e) => {
                         const rows = [...profile.business_licenses];
                         rows[idx] = { ...rows[idx], document_url: e.target.value };
                         setProfile({ ...profile, business_licenses: rows });
                       }} />
                <button
                  type="button" data-testid={`tax-license-remove-${idx}`}
                  onClick={() => setProfile({ ...profile,
                    business_licenses: profile.business_licenses.filter((_, i) => i !== idx) })}
                  className="rounded-sm border border-red-400 bg-red-500/10 px-2 py-1 text-[10.5px] text-red-600 hover:bg-red-500/20"
                >Remove</button>
              </div>
            ))}
          </div>

          <div className="mt-6">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-[12px] font-semibold">Annual state fees · right-to-do-business tax</div>
              <button
                type="button" data-testid="tax-fee-add"
                onClick={() => setProfile({ ...profile,
                  annual_state_fees: [...(profile.annual_state_fees || []),
                    { state: '', fee_kind: 'Right-to-do-business tax', amount_cents: 0, next_due_on: '', portal_url: '', filing_number: '' }] })}
                className="rounded-sm border border-primary/40 bg-primary/10 px-2 py-1 text-[10.5px] text-primary hover:bg-primary/20"
              >+ Add annual fee</button>
            </div>
            {(profile.annual_state_fees || []).length === 0 && (
              <div className="rounded-md border border-dashed border-border p-3 text-[11.5px] text-muted-foreground">
                No annual fees on file yet.
              </div>
            )}
            {(profile.annual_state_fees || []).map((fee, idx) => (
              <div key={idx} className="mb-2 grid gap-2 rounded-md border border-border p-2 md:grid-cols-3" data-testid={`tax-fee-row-${idx}`}>
                <input className={inputCls} placeholder="State" value={fee.state || ''}
                       data-testid={`tax-fee-state-${idx}`}
                       onChange={(e) => {
                         const val = e.target.value.toUpperCase();
                         const rows = [...profile.annual_state_fees];
                         rows[idx] = { ...rows[idx], state: val };
                         // iter-compliance-cal-p4 — Fee Preset Autocomplete:
                         // when the state matches a known preset AND the row
                         // has no portal_url yet, auto-suggest it.
                         const preset = presets.find((p) => p.state === val);
                         if (preset && !rows[idx].portal_url) {
                           rows[idx].portal_url = preset.template;
                           toast.success(`Auto-filled ${preset.label}.`);
                         }
                         setProfile({ ...profile, annual_state_fees: rows });
                       }} />
                <input className={inputCls} placeholder="Fee kind" value={fee.fee_kind || ''}
                       onChange={(e) => {
                         const rows = [...profile.annual_state_fees];
                         rows[idx] = { ...rows[idx], fee_kind: e.target.value };
                         setProfile({ ...profile, annual_state_fees: rows });
                       }} />
                <input className={inputCls} type="number" placeholder="Amount (cents)"
                       value={fee.amount_cents || 0}
                       data-testid={`tax-fee-amount-${idx}`}
                       onChange={(e) => {
                         const rows = [...profile.annual_state_fees];
                         rows[idx] = { ...rows[idx], amount_cents: parseInt(e.target.value || '0', 10) };
                         setProfile({ ...profile, annual_state_fees: rows });
                       }} />
                <input className={inputCls} placeholder="Last paid on YYYY-MM-DD"
                       value={fee.last_paid_on || ''}
                       onChange={(e) => {
                         const rows = [...profile.annual_state_fees];
                         rows[idx] = { ...rows[idx], last_paid_on: e.target.value };
                         setProfile({ ...profile, annual_state_fees: rows });
                       }} />
                <input className={inputCls} placeholder="Next due YYYY-MM-DD"
                       value={fee.next_due_on || ''}
                       data-testid={`tax-fee-nextdue-${idx}`}
                       onChange={(e) => {
                         const rows = [...profile.annual_state_fees];
                         rows[idx] = { ...rows[idx], next_due_on: e.target.value };
                         setProfile({ ...profile, annual_state_fees: rows });
                       }} />
                <input className={inputCls} placeholder="Filing / account number"
                       value={fee.filing_number || ''}
                       data-testid={`tax-fee-filing-${idx}`}
                       onChange={(e) => {
                         const rows = [...profile.annual_state_fees];
                         rows[idx] = { ...rows[idx], filing_number: e.target.value };
                         setProfile({ ...profile, annual_state_fees: rows });
                       }} />
                <select className={inputCls}
                        data-testid={`tax-fee-preset-${idx}`}
                        value=""
                        onChange={(e) => {
                          const preset = presets.find((p) => p.template === e.target.value);
                          if (!preset) return;
                          const rows = [...profile.annual_state_fees];
                          rows[idx] = { ...rows[idx],
                            portal_url: preset.template,
                            state: rows[idx].state || preset.state };
                          setProfile({ ...profile, annual_state_fees: rows });
                          toast.success(`Loaded ${preset.label}.`);
                        }}>
                  <option value="">Load state preset…</option>
                  {presets.map((p) => (
                    <option key={p.state + p.label} value={p.template} title={p.note}>
                      {p.label}
                    </option>
                  ))}
                </select>
                <input className={inputCls} placeholder="Portal URL (use {number} / {ein} / {state} for auto-fill)"
                       value={fee.portal_url || ''}
                       onChange={(e) => {
                         const rows = [...profile.annual_state_fees];
                         rows[idx] = { ...rows[idx], portal_url: e.target.value };
                         setProfile({ ...profile, annual_state_fees: rows });
                       }} />
                <button
                  type="button" data-testid={`tax-fee-remove-${idx}`}
                  onClick={() => setProfile({ ...profile,
                    annual_state_fees: profile.annual_state_fees.filter((_, i) => i !== idx) })}
                  className="rounded-sm border border-red-400 bg-red-500/10 px-2 py-1 text-[10.5px] text-red-600 hover:bg-red-500/20"
                >Remove</button>
              </div>
            ))}
          </div>

          <div className="mt-4 flex gap-2">
            <button type="button" onClick={save} disabled={busy}
                    data-testid="admin-tax-save-entity"
                    className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-[12.5px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
              <Save className="h-4 w-4" /> Save entity info
            </button>
          </div>
        </Section>
      )}

      {tab === 'calendar' && (
        <ComplianceCalendarTab http={http} reload={load} presets={presets}
                               onProfileState={() => setProfile({...profile})} />
      )}

      {tab === 'sales' && sales && (
        <Section title="Regional Sales Ledger" icon={Globe}>
          {sales.flags?.length > 0 && (
            <div className="mb-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-3" data-testid="tax-sales-flags">
              {sales.flags.map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-[12px] text-amber-700 dark:text-amber-300">
                  <AlertTriangle className="h-3.5 w-3.5" /> {f.message}
                </div>
              ))}
            </div>
          )}
          <div className="grid gap-4 md:grid-cols-2">
            <table className="w-full text-[12px]" data-testid="tax-sales-countries">
              <thead>
                <tr className="border-b border-border text-left text-[10.5px] uppercase tracking-wider text-muted-foreground">
                  <th className="py-1">Country</th><th>Orders</th><th className="text-right">Gross</th>
                </tr>
              </thead>
              <tbody>
                {sales.countries.map((c) => (
                  <tr key={c.country} className="border-b border-border/60">
                    <td className="py-1 font-semibold">{c.country}</td>
                    <td>{c.orders}</td>
                    <td className="text-right">${(c.gross_cents / 100).toFixed(2)}</td>
                  </tr>
                ))}
                {!sales.countries.length && <tr><td colSpan={3} className="py-2 text-muted-foreground">No sales recorded yet.</td></tr>}
              </tbody>
            </table>
            <table className="w-full text-[12px]" data-testid="tax-sales-states">
              <thead>
                <tr className="border-b border-border text-left text-[10.5px] uppercase tracking-wider text-muted-foreground">
                  <th className="py-1">US State</th><th>Orders</th><th className="text-right">Gross</th>
                </tr>
              </thead>
              <tbody>
                {sales.us_states.map((s) => (
                  <tr key={s.state} className="border-b border-border/60">
                    <td className="py-1 font-semibold">{s.state}</td>
                    <td>{s.orders}</td>
                    <td className="text-right">${(s.gross_cents / 100).toFixed(2)}</td>
                  </tr>
                ))}
                {!sales.us_states.length && <tr><td colSpan={3} className="py-2 text-muted-foreground">No US-state-tagged orders yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {tab === 'ebitda' && ebitda && (
        <Section title="EBITDA / EBITDAT Snapshot" icon={TrendingUp}>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Metric label="Revenue"      value={`$${ebitda.revenue_usd.toFixed(2)}`} />
            <Metric label="COGS"         value={`$${ebitda.cogs_usd.toFixed(2)}`} />
            <Metric label="EBITDA"       value={`$${ebitda.ebitda_usd.toFixed(2)}`} tone="good" />
            <Metric label="EBITDAT (post-tax)" value={`$${ebitda.ebitdat_usd.toFixed(2)}`} tone="good" />
            <Metric label="Margin %"     value={`${ebitda.margin_pct}%`} />
            <Metric label="Opex"         value={`$${ebitda.opex_usd.toFixed(2)}`} />
            <Metric label="Withholding"  value={`$${ebitda.withholding_usd.toFixed(2)}`} />
            <Metric label="Subs revenue" value={`$${ebitda.subs_revenue_usd.toFixed(2)}`} />
          </div>
          {ebitdaBook && (
            <div className="mt-4">
              <div className="mb-1 text-[11px] uppercase tracking-wider text-muted-foreground">Per-book EBITDA · from every completed order</div>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <Metric label="Orders"       value={ebitdaBook.orders} />
                <Metric label="Book gross"   value={`$${(ebitdaBook.gross_cents / 100).toFixed(2)}`} />
                <Metric label="Book COGS"    value={`$${(ebitdaBook.cogs_cents / 100).toFixed(2)}`} />
                <Metric label="Book EBITDA"  value={`$${(ebitdaBook.ebitda_cents / 100).toFixed(2)}`} tone="good" />
              </div>
              {ebitdaBook.worst_books?.length > 0 && (
                <div className="mt-3">
                  <div className="mb-1 text-[11px] uppercase tracking-wider text-muted-foreground">Least profitable titles</div>
                  <table className="w-full text-[12px]" data-testid="tax-worst-books">
                    <thead>
                      <tr className="border-b border-border text-left text-[10.5px] uppercase tracking-wider text-muted-foreground">
                        <th className="py-1">Book</th><th>Orders</th><th className="text-right">Gross</th><th className="text-right">EBITDA</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ebitdaBook.worst_books.map((b) => (
                        <tr key={b.book_id} className="border-b border-border/60">
                          <td className="py-1 truncate">{b.book_id}</td>
                          <td>{b.orders}</td>
                          <td className="text-right">${(b.gross_cents / 100).toFixed(2)}</td>
                          <td className={`text-right ${b.ebitda_cents < 0 ? 'text-red-500' : ''}`}>${(b.ebitda_cents / 100).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </Section>
      )}

      {tab === 'guard' && guard && (
        <Section title="Min-Store-Price Guard" icon={AlertTriangle}>
          <div className="mb-2 text-[12px]">
            Floor: <strong>${(guard.min_store_price_cents / 100).toFixed(2)}</strong> · Target margin: <strong>{guard.target_margin_pct}%</strong>
          </div>
          {guard.below_floor_count > 0 ? (
            <div className="mb-3 rounded-md border border-red-500/40 bg-red-500/10 p-3 text-[12px] text-red-700 dark:text-red-300">
              {guard.below_floor_count} book(s) are priced below the viability floor.
              <div className="mt-2 flex gap-2">
                <button
                  className="rounded-sm border border-red-400 bg-red-500/20 px-2 py-1 text-[11px] font-semibold hover:bg-red-500/30"
                  onClick={() => enforceMinPrice(true)}
                  data-testid="admin-tax-min-price-dryrun"
                >
                  Preview snap
                </button>
                <button
                  className="rounded-sm bg-red-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-red-700"
                  onClick={() => enforceMinPrice(false)}
                  data-testid="admin-tax-min-price-apply"
                >
                  Snap prices up
                </button>
              </div>
            </div>
          ) : (
            <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3 text-[12px] text-emerald-700 dark:text-emerald-300">
              All storefront books meet the viability floor.
            </div>
          )}
          <table className="w-full text-[12px]" data-testid="tax-guard-books">
            <thead>
              <tr className="border-b border-border text-left text-[10.5px] uppercase tracking-wider text-muted-foreground">
                <th className="py-1">Title</th><th>Author</th><th className="text-right">Price</th><th className="text-right">Floor</th><th></th>
              </tr>
            </thead>
            <tbody>
              {guard.books.map((b) => (
                <tr key={b.book_id} className={`border-b border-border/60 ${b.ok ? '' : 'bg-red-500/5'}`}>
                  <td className="py-1 truncate max-w-xs">{b.title || b.book_id}</td>
                  <td className="text-muted-foreground">{b.author || '—'}</td>
                  <td className="text-right">${(b.price_cents / 100).toFixed(2)}</td>
                  <td className="text-right">${(b.floor_cents / 100).toFixed(2)}</td>
                  <td className="text-right">
                    <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${b.ok
                      ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300'
                      : 'bg-red-500/20 text-red-700 dark:text-red-300'}`}>
                      {b.ok ? 'OK' : `−$${(b.shortfall_cents / 100).toFixed(2)}`}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}
    </div>
  );
}

function Metric({ label, value, tone }) {
  return (
    <div className={`rounded-md border p-3 ${tone === 'good' ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-border bg-panel-header/40'}`}>
      <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-[15.5px] font-semibold tracking-tight">{value}</div>
    </div>
  );
}

// ------------------------------ Compliance Calendar tab ---------------------

function ComplianceCalendarTab({ http, reload, presets = [] }) {
  const [feed, setFeed] = useState(null);
  const [log, setLog]   = useState([]);
  const [spend, setSpend] = useState(null);
  const [trends, setTrends] = useState(null);
  const [prefs, setPrefs] = useState({ shift_to_business_day_default: false });
  const [busy, setBusy] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  const refresh = useCallback(async () => {
    setBusy(true);
    try {
      const [c, l, s, t, p] = await Promise.all([
        http.get('/admin/compliance-calendar'),
        http.get('/admin/compliance-calendar/reminders/log'),
        http.get('/admin/compliance-calendar/fee-spend-by-year'),
        http.get('/admin/compliance-calendar/cost-trends'),
        http.get('/admin/compliance-calendar/prefs'),
      ]);
      setFeed(c.data);
      setLog(l.data.reminders || []);
      setSpend(s.data);
      setTrends(t.data);
      setPrefs(p.data.prefs || { shift_to_business_day_default: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load calendar.');
    } finally {
      setBusy(false);
    }
  }, [http]);
  useEffect(() => { refresh(); }, [refresh]);

  const setShiftPref = async (val) => {
    try {
      await http.put('/admin/compliance-calendar/prefs',
        { shift_to_business_day_default: val });
      setPrefs({ shift_to_business_day_default: val });
      toast.success(val
        ? 'Renewals will auto-shift to a business day by default.'
        : 'Auto-shift disabled — you\'ll be prompted each time.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save preference.');
    }
  };

  const dryRun = async () => {
    try {
      const r = await http.post('/admin/compliance-calendar/reminders/run-now', { dry_run: true });
      toast.success(`${r.data.would_send} reminder(s) would fire on the next 08:00 UTC tick.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Dry-run failed.');
    }
  };

  const fireNow = async () => {
    if (!(await openConfirm({
      title: 'Send today\'s license/fee reminder email?',
      description: 'Delivers to d.willis@crownnrose.com now.',
      confirmLabel: 'Send now',
    }))) return;
    try {
      await http.post('/admin/compliance-calendar/reminders/run-now', { dry_run: false });
      toast.success('Reminder email dispatched.');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed.');
    }
  };

  const uploadDoc = async (idx, file) => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    try {
      await http.post(`/admin/compliance-calendar/license/${idx}/document`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('License PDF attached.');
      await refresh();
      await reload?.();
      // iter-compliance-cal-p4 — Renewal wizard: after a successful
      // upload, offer to bump the expiry by +1 yr in the same flow.
      if (await openConfirm({
        title: 'PDF attached ✓',
        description: 'Extend the expiry date by +1 year to complete the renewal?',
        confirmLabel: 'Extend +1 year',
      })) {
        try {
          const r = await http.post(`/admin/compliance-calendar/license/${idx}/extend`, { years: 1 });
          toast.success(`Renewal complete — expires ${r.data.new_expiry}.`);
          await refresh(); await reload?.();
        } catch (e) {
          toast.error(e?.response?.data?.detail || 'Extend after upload failed.');
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed.');
    }
  };

  const removeDoc = async (idx) => {
    if (!(await openConfirm({
      title: 'Remove attached document from this license?',
      tone: 'destructive',
    }))) return;
    try {
      await http.delete(`/admin/compliance-calendar/license/${idx}/document`);
      toast.success('Document removed.');
      await refresh(); await reload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed.');
    }
  };

  const extendLicense = async (idx) => {
    const raw = await openPrompt({
      title: 'Extend this licence',
      description: 'Enter a whole number of years between 1 and 10.',
      placeholder: 'Years',
      initial: '1',
      minLength: 1,
    });
    if (raw === null) return;
    const years = parseInt(raw, 10);
    if (!Number.isFinite(years) || years < 1 || years > 10) {
      toast.error('Enter a whole number between 1 and 10.');
      return;
    }
    // iter-p4-shift-default — skip the second confirm() when the admin
    // has opted-in to auto-shift as a default.
    const shift = prefs.shift_to_business_day_default
      ? true
      : await openConfirm({
          title: 'Shift the new expiry?',
          description: 'Move to the prior business day if it lands on a weekend or federal holiday?',
          confirmLabel: 'Shift back',
          cancelLabel: 'Keep as-is',
        });
    try {
      const r = await http.post(`/admin/compliance-calendar/license/${idx}/extend`,
        { years, shift_to_business_day: shift });
      const shifted = r.data.shifted_to_business_day ? ' (shifted to a business day)' : '';
      toast.success(`Expires now ${r.data.new_expiry} (+${years} yr)${shifted}.`);
      await refresh(); await reload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Extend failed.');
    }
  };

  const markFeePaid = async (idx) => {
    if (!(await openConfirm({
      title: 'Mark this fee paid today?',
      description: 'This advances the next due date by 12 months.',
      confirmLabel: 'Mark paid',
    }))) return;
    const shift = prefs.shift_to_business_day_default
      ? true
      : await openConfirm({
          title: 'Shift the next due date?',
          description: 'Push to the next business day if it lands on a weekend or federal holiday?',
          confirmLabel: 'Shift forward',
          cancelLabel: 'Keep as-is',
        });
    try {
      const r = await http.post(`/admin/compliance-calendar/fee/${idx}/mark-paid`,
        { months: 12, shift_to_business_day: shift });
      const shifted = r.data.shifted_to_business_day ? ' (shifted to a business day)' : '';
      toast.success(`Paid ${r.data.last_paid_on}. Next due ${r.data.next_due_on}${shifted}.`);
      await refresh(); await reload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Mark-paid failed.');
    }
  };

  const exportCsv = async () => {
    try {
      const r = await http.get('/admin/compliance-calendar/export.csv', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url; a.download = `compliance-${stamp}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('CSV downloaded.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export failed.');
    }
  };

  const downloadDoc = async (idx) => {
    try {
      const r = await http.get(`/admin/compliance-calendar/license/${idx}/document`,
        { responseType: 'blob' });
      const blob = new Blob([r.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `license-${idx}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Download failed.');
    }
  };

  if (!feed) return (
    <div className="flex h-40 items-center justify-center text-[12px] text-muted-foreground">
      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading calendar…
    </div>
  );

  const cardTone = (days) => {
    if (days < 0) return 'border-red-500/60 bg-red-500/10 text-red-800 dark:text-red-200';
    if (days <= 3) return 'border-red-500/40 bg-red-500/5';
    if (days <= 30) return 'border-amber-500/40 bg-amber-500/5';
    return 'border-border bg-panel-header/30';
  };

  return (
    <section className="rounded-lg border border-border bg-card p-4" data-testid="compliance-calendar-panel">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[13px] font-semibold tracking-tight">
          <Calendar className="h-3.5 w-3.5 text-primary" /> Compliance Calendar
        </div>
        <label className="inline-flex cursor-pointer items-center gap-1.5 text-[11px] text-muted-foreground"
               data-testid="compliance-shift-default-toggle">
          <input type="checkbox" checked={prefs.shift_to_business_day_default}
                 onChange={(e) => setShiftPref(e.target.checked)} />
          Auto-shift renewals to a business day
        </label>
      </div>
      <div className="mb-3 flex items-center justify-end gap-1.5">
        <div className="flex gap-1.5">
          <button type="button" onClick={exportCsv} disabled={busy}
                  data-testid="compliance-export-csv"
                  className="inline-flex items-center gap-1 rounded-sm border border-border bg-background px-2 py-1 text-[11px] hover:bg-secondary">
            <Download className="h-3 w-3" /> Export CSV
          </button>
          <button type="button" onClick={() => setShowSearch(true)}
                  data-testid="compliance-import-btn"
                  className="inline-flex items-center gap-1 rounded-sm border border-border bg-background px-2 py-1 text-[11px] hover:bg-secondary">
            <Search className="h-3 w-3" /> Import from OpenCorporates
          </button>
          <button type="button" onClick={dryRun} disabled={busy}
                  data-testid="compliance-dryrun-btn"
                  className="inline-flex items-center gap-1 rounded-sm border border-border bg-background px-2 py-1 text-[11px] hover:bg-secondary">
            Dry-run
          </button>
          <button type="button" onClick={fireNow} disabled={busy}
                  data-testid="compliance-fire-btn"
                  className="inline-flex items-center gap-1 rounded-sm bg-primary px-2 py-1 text-[11px] font-semibold text-primary-foreground hover:bg-primary/90">
            Send reminder now
          </button>
        </div>
      </div>

      {trends && trends.flagged && trends.flagged.length > 0 && (
        <div className="mb-3 rounded-md border border-amber-500/50 bg-amber-500/10 p-3" data-testid="compliance-cost-trend-warnings">
          <div className="mb-1 text-[11.5px] font-semibold text-amber-900 dark:text-amber-100">
            ⚠ {trends.flagged.length} state fee amount{trends.flagged.length === 1 ? '' : 's'} jumped year-over-year
          </div>
          <ul className="text-[11px] space-y-0.5">
            {trends.flagged.slice(0, 5).map((t, i) => (
              <li key={i} data-testid={`cost-trend-flag-${t.state}-${t.year}`}>
                <b>{t.state}</b> · {t.fee_kind} — {t.previous_year}: ${(t.previous_cents / 100).toFixed(2)} → {t.year}: <b>${(t.current_cents / 100).toFixed(2)}</b>
                <span className={`ml-1 font-mono ${t.direction === 'up' ? 'text-red-700 dark:text-red-300' : 'text-emerald-700'}`}>
                  ({t.direction === 'up' ? '+' : ''}{t.delta_pct}%)
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mb-3 grid grid-cols-2 gap-2 md:grid-cols-4" data-testid="compliance-counters">
        <Metric label="Overdue"  value={feed.counts.overdue}  tone={feed.counts.overdue ? undefined : 'good'} />
        <Metric label="Due ≤30d" value={feed.counts.due_soon} />
        <Metric label="Upcoming" value={feed.counts.upcoming} />
        <Metric label="Later"    value={feed.counts.later} />
      </div>

      {feed.items.length === 0 ? (
        <div className="rounded-md border border-dashed border-border p-6 text-center text-[12px] text-muted-foreground">
          No licenses or state fees on file with a date set. Add expiry / due dates in the Business Entity tab to populate the countdown.
        </div>
      ) : (
        <>
          {spend && spend.years.length > 0 && (
            <div className="mb-3 rounded-md border border-border bg-panel-header/30 p-3" data-testid="compliance-spend-chart">
              <div className="mb-1 flex items-center justify-between text-[11px] uppercase tracking-wider text-muted-foreground">
                <span>Compliance spend by year</span>
                <span className="font-mono">Total ${(spend.total_cents / 100).toFixed(2)}</span>
              </div>
              <div className="flex items-end gap-3 pt-2" style={{ height: 90 }}>
                {(() => {
                  const max = Math.max(...spend.years.map((y) => y.total_cents), 1);
                  return spend.years.map((y) => (
                    <div key={y.year} className="flex flex-1 flex-col items-center gap-1" data-testid={`spend-bar-${y.year}`}>
                      <div className="text-[9.5px] font-mono text-muted-foreground">
                        ${(y.total_cents / 100).toFixed(0)}
                      </div>
                      <div className="w-full min-w-[24px] rounded-t-sm bg-primary/70"
                           style={{ height: `${Math.max(4, (y.total_cents / max) * 60)}px` }}
                           title={`${y.count} payment${y.count === 1 ? '' : 's'} · $${(y.total_cents / 100).toFixed(2)}`} />
                      <div className="text-[10.5px] font-semibold">{y.year}</div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}
          <ul className="grid gap-2 md:grid-cols-2" data-testid="compliance-items">
          {feed.items.map((it) => (
            <li key={`${it.kind}-${it.index}`}
                data-testid={`compliance-item-${it.kind}-${it.index}`}
                className={`rounded-md border p-3 ${cardTone(it.days_out)}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="text-[12.5px] font-semibold truncate">{it.label}</div>
                <span className="rounded-full bg-background/80 px-1.5 py-0.5 text-[10px] font-mono">
                  {it.days_out < 0 ? `${-it.days_out}d overdue` : `T-${it.days_out}d`}
                </span>
              </div>
              <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
                <span className="capitalize">{it.kind.replace('_', ' ')}</span>
                <span>·</span>
                <span>{it.on}</span>
                {typeof it.amount_cents === 'number' && it.amount_cents > 0 && (
                  <>
                    <span>·</span>
                    <span className="font-mono">${(it.amount_cents / 100).toFixed(2)}</span>
                  </>
                )}
              </div>
              {it.sunset && (
                <div className="mt-1.5 rounded-sm border border-amber-500/50 bg-amber-500/10 px-2 py-1 text-[10.5px] text-amber-800 dark:text-amber-200"
                     data-testid={`compliance-sunset-${it.kind}-${it.index}`}>
                  ⚠ {it.sunset.message}
                </div>
              )}
              {it.kind === 'license_expiring' && (
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <label className="inline-flex cursor-pointer items-center gap-1 rounded-sm border border-primary/40 bg-primary/10 px-2 py-1 text-[10.5px] text-primary hover:bg-primary/20">
                    <Upload className="h-3 w-3" />
                    {it.has_document ? 'Replace PDF' : 'Attach PDF'}
                    <input type="file" accept=".pdf,image/png,image/jpeg,image/webp"
                           data-testid={`compliance-upload-${it.index}`}
                           className="hidden"
                           onChange={(e) => uploadDoc(it.index, e.target.files?.[0])} />
                  </label>
                  {it.has_document && (
                    <>
                      <button type="button" onClick={() => downloadDoc(it.index)}
                              data-testid={`compliance-download-${it.index}`}
                              className="inline-flex items-center gap-1 rounded-sm border border-border bg-background px-2 py-1 text-[10.5px] hover:bg-secondary">
                        <Download className="h-3 w-3" /> Open
                      </button>
                      <button type="button" onClick={() => removeDoc(it.index)}
                              data-testid={`compliance-doc-remove-${it.index}`}
                              className="inline-flex items-center gap-1 rounded-sm border border-red-400 bg-red-500/10 px-2 py-1 text-[10.5px] text-red-600 hover:bg-red-500/20">
                        <X className="h-3 w-3" /> Remove
                      </button>
                    </>
                  )}
                  <button type="button" onClick={() => extendLicense(it.index)}
                          data-testid={`compliance-extend-${it.index}`}
                          className="inline-flex items-center gap-1 rounded-sm border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[10.5px] text-emerald-700 hover:bg-emerald-500/20 dark:text-emerald-300">
                    Extend +1 yr
                  </button>
                </div>
              )}
              {it.kind === 'fee_due' && (
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {it.portal_url ? (
                    <a href={it.portal_url} target="_blank" rel="noreferrer"
                       data-testid={`compliance-portal-${it.index}`}
                       className="inline-flex items-center gap-1 rounded-sm bg-primary px-2 py-1 text-[10.5px] font-semibold text-primary-foreground hover:bg-primary/90">
                      <Globe className="h-3 w-3" />
                      Open filing portal{it.filing_number ? ` · ${it.filing_number}` : ''}
                    </a>
                  ) : (
                    <span className="text-[10.5px] text-muted-foreground">
                      Add a portal URL in the Business Entity tab to enable one-click filing.
                    </span>
                  )}
                  <button type="button" onClick={() => markFeePaid(it.index)}
                          data-testid={`compliance-mark-paid-${it.index}`}
                          className="inline-flex items-center gap-1 rounded-sm border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[10.5px] text-emerald-700 hover:bg-emerald-500/20 dark:text-emerald-300">
                    Mark paid
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
        </>
      )}

      <div className="mt-5">
        <div className="mb-2 text-[11px] uppercase tracking-wider text-muted-foreground">
          Recent reminder emails (T-30 / T-14 / T-3)
        </div>
        {log.length === 0 ? (
          <div className="rounded-md border border-dashed border-border p-3 text-[11px] text-muted-foreground">
            No reminders have fired yet. The cron runs daily at 08:00 UTC and emails <b>d.willis@crownnrose.com</b>.
          </div>
        ) : (
          <table className="w-full text-[12px]" data-testid="compliance-log">
            <thead>
              <tr className="border-b border-border text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="py-1">Sent at</th><th>Kind</th><th>Item</th><th>Countdown</th>
              </tr>
            </thead>
            <tbody>
              {log.slice(0, 20).map((r) => (
                <tr key={r.id} className="border-b border-border/60">
                  <td className="py-1 font-mono text-[10.5px]">{(r.at || '').slice(0, 16).replace('T', ' ')}</td>
                  <td className="capitalize">{(r.kind || '').replace('_', ' ')}</td>
                  <td className="truncate">{r.label}</td>
                  <td className="font-mono">T-{r.days_out}d</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showSearch && (
        <OpenCorporatesSearchDialog
          http={http}
          onClose={() => setShowSearch(false)}
          onImported={async () => {
            setShowSearch(false);
            await refresh();
            await reload?.();
          }}
        />
      )}
    </section>
  );
}

function OpenCorporatesSearchDialog({ http, onClose, onImported }) {
  const [q, setQ] = useState('');
  const [jurisdiction, setJur] = useState('us_fl');
  const [hits, setHits] = useState([]);
  const [busy, setBusy] = useState(false);

  const doSearch = async () => {
    if (!q.trim()) { toast.error('Enter a company name to search.'); return; }
    setBusy(true);
    try {
      const r = await http.get('/admin/compliance-calendar/opencorporates/search',
        { params: { q, jurisdiction } });
      setHits(r.data.results || []);
      if (!r.data.results?.length) toast.info('No matches found.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Search failed.');
    } finally { setBusy(false); }
  };

  const importHit = async (h) => {
    try {
      await http.post('/admin/compliance-calendar/opencorporates/import', {
        company_number:     h.company_number,
        jurisdiction_code:  h.jurisdiction_code,
        name:               h.name,
        company_type:       h.company_type || '',
        incorporation_date: h.incorporation_date || '',
        registry_url:       h.registry_url || '',
      });
      toast.success(`Imported ${h.name}.`);
      onImported();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
         data-testid="compliance-opencorporates-dialog">
      <div className="w-full max-w-2xl rounded-lg border border-border bg-card p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-[13px] font-semibold">Import company registration · OpenCorporates</div>
          <button type="button" onClick={onClose} className="rounded-sm p-1 hover:bg-secondary">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="mb-3 grid grid-cols-3 gap-2">
          <input className={`${inputCls} col-span-2`} placeholder="Company name"
                 value={q} onChange={(e) => setQ(e.target.value)}
                 data-testid="opencorp-search-q"
                 onKeyDown={(e) => e.key === 'Enter' && doSearch()} />
          <input className={inputCls} placeholder="Jurisdiction (e.g. us_fl, gb)"
                 value={jurisdiction} onChange={(e) => setJur(e.target.value)}
                 data-testid="opencorp-search-jur" />
        </div>
        <div className="mb-3 flex gap-2">
          <button type="button" onClick={doSearch} disabled={busy}
                  data-testid="opencorp-search-run"
                  className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-[11.5px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Search className="h-3 w-3" />}
            Search OpenCorporates
          </button>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {hits.length === 0 ? (
            <div className="rounded-md border border-dashed border-border p-4 text-center text-[11.5px] text-muted-foreground">
              Enter a company name and jurisdiction, then Search. Results come from the OpenCorporates public API.
            </div>
          ) : (
            <ul className="space-y-1.5">
              {hits.map((h, i) => (
                <li key={`${h.company_number}-${i}`}
                    data-testid={`opencorp-hit-${i}`}
                    className="rounded-md border border-border p-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate text-[12.5px] font-semibold">{h.name}</div>
                      <div className="text-[10.5px] text-muted-foreground">
                        {h.company_number} · {h.jurisdiction_code}
                        {h.incorporation_date && ` · Inc. ${h.incorporation_date}`}
                        {h.current_status && ` · ${h.current_status}`}
                      </div>
                    </div>
                    <button type="button" onClick={() => importHit(h)}
                            data-testid={`opencorp-import-${i}`}
                            className="rounded-sm border border-primary/40 bg-primary/10 px-2 py-1 text-[10.5px] text-primary hover:bg-primary/20">
                      Import
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
